import { useLang } from "../lib/i18n";
import { EBOOK } from "../lib/profile";
import { Check, BookOpen, ShoppingBag } from "lucide-react";
import useReveal from "../hooks/useReveal";

export const Ebook = () => {
    const { t } = useLang();
    const { ref, visible } = useReveal();

    return (
        <section
            id="ebook"
            ref={ref}
            className={`relative py-24 md:py-32 border-t border-navy/10 reveal ${
                visible ? "visible" : ""
            }`}
            data-testid="ebook-section"
        >
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-20 -left-40 w-[480px] h-[480px] rounded-full bg-warm/15 blur-3xl" />
                <div className="absolute bottom-0 -right-40 w-[420px] h-[420px] rounded-full bg-accentblue/10 blur-3xl" />
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16 items-center">
                    {/* LEFT — Cover */}
                    <div className="lg:col-span-5" data-testid="ebook-cover-wrap">
                        <div className="relative max-w-md mx-auto">
                            <div className="absolute -top-6 -left-6 w-20 h-20 rounded-full bg-warm" />
                            <div className="absolute -bottom-6 -right-6 w-24 h-24 border-2 border-navy" />

                            <div className="relative">
                                <div className="relative aspect-[3/4] shadow-[0_30px_60px_-20px_rgba(10,25,47,0.45)] hover:scale-[1.02] transition-transform duration-700">
                                    <div className="absolute left-0 top-0 bottom-0 w-3 bg-gradient-to-r from-navy-900 to-navy" />
                                    <img
                                        src={EBOOK.coverImage}
                                        alt="Arquitecto Digital — Ebook por Gilberto Veliz"
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover"
                                        data-testid="ebook-cover-img"
                                    />
                                    <div className="absolute -right-1 top-1 bottom-1 w-1 bg-cream-200 shadow-md" />
                                </div>
                            </div>

                            <div className="mt-6 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.25em] text-navy/60">
                                <BookOpen className="w-3.5 h-3.5 text-warm-600" />
                                {t.ebook.badge}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — Copy + CTA */}
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-navy/60">
                            <span className="tick" />
                            {t.ebook.kicker}
                        </div>
                        <h2
                            className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05] tracking-tight"
                            data-testid="ebook-heading"
                        >
                            {t.ebook.heading}
                        </h2>
                        <p
                            className="mt-6 text-navy/70 text-lg max-w-2xl leading-relaxed"
                            data-testid="ebook-subheading"
                        >
                            {t.ebook.subheading}
                        </p>

                        <ul className="mt-8 space-y-3" data-testid="ebook-bullets">
                            {t.ebook.bullets.map((b, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-3 text-navy/85 text-[15px]"
                                >
                                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-warm flex-shrink-0">
                                        <Check className="w-3 h-3 text-navy" strokeWidth={3} />
                                    </span>
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
                            <a
                                href={EBOOK.checkoutUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-flex items-center justify-center gap-3 bg-navy text-cream px-7 py-4 text-sm md:text-base tracking-wide hover:bg-warm-600 transition-colors shadow-[0_18px_30px_-15px_rgba(10,25,47,0.5)]"
                                data-testid="ebook-buy-button"
                            >
                                <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                                <span>{t.ebook.cta}</span>
                                <span className="transition-transform group-hover:translate-x-1">
                                    →
                                </span>
                            </a>
                            <div className="flex flex-col">
                                <div className="text-xs text-navy/55 tracking-wide">
                                    {t.ebook.ctaSub}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Ebook;
